import { useEffect, useState } from "react";
import { useScope } from "~/hooks/useScope";
import {
  ScoreRating,
  useGetTotalAssetsQuery,
  useGetWorkflowAssetsQuery,
} from "~/operations";
import { useWorkflowAssetsFilterContext } from "./useWorkflowAssetsFilterContext";
import { GridSortModel } from "@mui/x-data-grid";
import { useDataGridPagination } from "~/hooks/useDataGridPagination";

const DEFAULT_PAGE_SIZE = 5;

export function useWorkflowAssets({ workflowMrn }: { workflowMrn: string }) {
  const { spaceScope } = useScope();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "risk_score",
      sort: "desc",
    },
  ]);
  const [searchFilters, setSearchFilters] = useState<string[]>([]);
  const [riskFilter, setRiskFilter] = useState<ScoreRating[]>([]);

  const { filterForm, filterVariables } = useWorkflowAssetsFilterContext({
    scopeMrn: spaceScope?.mrn || "",
    workflowMrn,
  });

  useEffect(() => {
    const searchFilters = [
      ...(filterVariables.labels ?? []),
      ...(filterVariables.annotations ?? []),
      ...(filterVariables.queryTerms ?? []),
    ];
    setSearchFilters(searchFilters);
    setRiskFilter(filterVariables.rating ?? []);
  }, [filterVariables]);

  // Use state to track assetsConnection across renders
  const [assetsConnection, setAssetsConnection] = useState<{
    pageInfo?: { endCursor?: string | null; hasNextPage?: boolean };
    totalCount?: number;
  }>({});

  // Pagination hook reads cursor from URL
  const pagination = useDataGridPagination({
    prefix: "workflow",
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageInfo: assetsConnection.pageInfo,
    totalCount: assetsConnection.totalCount,
    resetDeps: [searchFilters, riskFilter],
  });

  const { data, error, loading } = useGetWorkflowAssetsQuery({
    variables: {
      workflowMrn,
      scopeMrn: spaceScope?.mrn || "",
      first: pagination.first,
      after: pagination.after,
      queryTerms: searchFilters,
      rating: riskFilter,
    },
    fetchPolicy: "cache-and-network",
  });

  // Update assetsConnection state when data arrives
  useEffect(() => {
    if (data?.assets?.__typename === "AssetsConnection") {
      setAssetsConnection({
        pageInfo: data.assets.pageInfo,
        totalCount: data.assets.totalCount,
      });
    }
  }, [data]);

  const assets =
    data?.assets?.__typename === "AssetsConnection" ? data.assets.edges : [];

  const { data: totalCountData } = useGetTotalAssetsQuery({
    variables: {
      scopeMrn: spaceScope?.mrn || "",
      workflowMrn,
    },
    skip: !spaceScope?.mrn,
  });

  return {
    loading,
    error,
    space: spaceScope,
    assets: assets ?? [],
    totalCount: totalCountData?.assets?.totalCount || 0,
    sortModel,
    setSortModel,
    filters: {
      filterForm,
      filterVariables,
    },
    // Pagination
    paginationModel: pagination.paginationModel,
    onPaginationModelChange: pagination.onPaginationModelChange,
    rowCount: pagination.rowCount,
    hasNextPage: pagination.hasNextPage,
    goToNextPage: pagination.goToNextPage,
  };
}
